import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import {
  ArrowLeft,
  MapPin,
  Phone,
  MessageCircle,
  Mail,
  Info,
  CheckCircle,
  Clock,
  Search
} from "lucide-react";

const SearchResults = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search).get("q") || "";

  const [events, setEvents] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedCity, setSelectedCity] = useState("");

  const [bookingDetails, setBookingDetails] = useState(null);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  const closeBtnRef = useRef(null);

  useEffect(() => {
    axios.get("https://ezer-eventapp.onrender.com/events")
      .then(res => setEvents(res.data))
      .catch(err => console.log(err));
  }, []);

  // Price Calculation Helper
  const getStartingPrice = (vendor) => {
    let min = Infinity;
    vendor.packages?.forEach(evt => {
      evt.packages?.forEach(pkg => {
        if (pkg.price < min) min = pkg.price;
      });
    });
    return min === Infinity ? 0 : min;
  };

  // Extract Unique Cities
  const allCities = [...new Set(events.flatMap(e => e.locations?.map(l => l.city) || []))];

  // Filter Logic
  const filteredEvents = events.filter(event => {
    const matchesSearch =
      event.name?.toLowerCase().includes(query.toLowerCase()) ||
      event.description?.toLowerCase().includes(query.toLowerCase()) ||
      event.categories?.some(cat => cat.toLowerCase().includes(query.toLowerCase())) ||
      event.packages?.some(p => p.eventName?.toLowerCase().includes(query.toLowerCase()));

    const matchesLocation = selectedCity ? event.locations?.some(loc => loc.city === selectedCity) : true;
    return matchesSearch && matchesLocation;
  });

  // Booking Submit Handler
  const submitHandler = (e) => {
    e.preventDefault();
    setSubmitStatus("loading");
    const formData = new FormData(e.target);
    const vendorLoc = selectedVendor?.locations?.length > 0
      ? `${selectedVendor.locations[0].city}, ${selectedVendor.locations[0].state}`
      : "Location not specified";

    const orderData = {
      orderId: `ORD-${Date.now().toString().slice(-6)}`,
      name: formData.get("name"),
      mobile: formData.get("phone"),
      address: `Vendor: ${selectedVendor.name} | Location: ${vendorLoc} | User Msg: ${formData.get("message")}`,
      variants: [{
        id: selectedVendor._id,
        name: bookingDetails.eventName,
        variant: { weight: bookingDetails.packageName, price: bookingDetails.price.toString() },
        qty: 1,
        subtotal: bookingDetails.price
      }],
      total: bookingDetails.price,
      date: new Date().toLocaleString(),
      status: "Pending"
    };

    axios.post("https://ezer-eventapp.onrender.com/orders", orderData)
      .then(() => {
        setSubmitStatus("success");
        setTimeout(() => {
          document.querySelector('[data-bs-dismiss="modal"]').click();
          setSubmitStatus("idle");
          e.target.reset();
        }, 2000);
      })
      .catch(() => {
        setSubmitStatus("error");
        setServerError("Booking failed");
      });
  };

  return (
    <div className="container py-5 mt-5">
      {/* HEADER & NAVIGATION */}
      <div className="row mb-4 align-items-center">
        <div className="col-md-6">
          {selectedVendor ? (
            <button className="btn btn-light mb-2" onClick={() => setSelectedVendor(null)}>
              <ArrowLeft size={16} /> Back to Search
            </button>
          ) : (
            <h2 className="fw-bold">Results for "{query}"</h2>
          )}
          {selectedVendor && <h2 className="fw-bold">{selectedVendor.name}</h2>}
        </div>

        {/* LOCATION FILTER - Hidden on detail page */}
        {!selectedVendor && (
          <div className="col-md-6">
            <div className="input-group shadow-sm">
              <span className="input-group-text bg-white border-end-0">
                <MapPin size={18} className="text-muted" />
              </span>
              <select
                className="form-select border-start-0 ps-0"
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
              >
                <option value="">All Locations</option>
                {allCities.map((city, i) => <option key={i} value={city}>{city}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* VENDOR LIST VIEW */}
  {!selectedVendor && (
  <div className="row">
    {filteredEvents.length > 0 ? (
      filteredEvents.map((vendor) => (
        <div className="col-md-4 mb-4" key={vendor._id}>
          <div
            className="card shadow-sm h-100 border-0"
            style={{ cursor: "pointer" }}
            onClick={() => setSelectedVendor(vendor)}
          >

            {/* Image Carousel */}
            <div
              id={`carousel-${vendor._id}`}
              className="carousel slide"
              data-bs-ride="carousel"
              data-bs-interval="1500"
            >

              <div className="carousel-inner rounded-top">

                {vendor.images?.map((img, index) => (
                  <div
                    key={index}
                    className={`carousel-item ${index === 0 ? "active" : ""}`}
                  >
                    <img
                      src={img}
                      className="d-block w-100"
                      style={{ height: 220, objectFit: "cover" }}
                      alt="vendor"
                    />
                  </div>
                ))}

              </div>

              {/* LEFT ARROW */}

              <button
                className="carousel-control-prev"
                type="button"
                data-bs-target={`#carousel-${vendor._id}`}
                data-bs-slide="prev"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="carousel-control-prev-icon"></span>
              </button>

              {/* RIGHT ARROW */}

              <button
                className="carousel-control-next"
                type="button"
                data-bs-target={`#carousel-${vendor._id}`}
                data-bs-slide="next"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="carousel-control-next-icon"></span>
              </button>

            </div>

            <div className="card-body">

              <div className="d-flex justify-content-between align-items-start mb-2">
                <h5 className="mb-0">{vendor.name}</h5>

                <span className="badge bg-light text-dark border">
                  <MapPin size={12} /> {vendor.locations?.[0]?.city}
                </span>
              </div>

              <p className="small text-muted text-truncate-2 mb-3">
                {vendor.description}
              </p>

              <div className="d-flex justify-content-between align-items-center">

                <h6 className="text-primary fw-bold mb-0">
                  Starts ₹{getStartingPrice(vendor)}
                </h6>

                <button
                  className="btn btn-dark btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedVendor(vendor);
                  }}
                >
                  View Details
                </button>

              </div>

            </div>

          </div>
        </div>
      ))
    ) : (
      <div className="text-center py-5">
        <Info size={48} className="text-muted mb-3" />
        <h4>No vendors match your search.</h4>
        <p className="text-muted">Try a different keyword or location.</p>
      </div>
    )}
  </div>
)}

      {/* VENDOR DETAIL VIEW */}
      {selectedVendor && (
        <div className="row">
          {/* LEFT CONTENT */}
          <div className="col-lg-8">
            <div className="mb-4">
              <h5>About {selectedVendor.name}</h5>
              <p className="text-muted">{selectedVendor.description}</p>
            </div>

            {selectedVendor.packages?.map((eventGroup, i) => (
              <div key={i} className="mb-5">
                <h4 className="text-primary mb-3 border-bottom pb-2">{eventGroup.eventName} Packages</h4>
                <div className="row">
                  {eventGroup.packages.map((pkg, j) => (
                    <div className="col-md-6 mb-3" key={j}>
                      <div className="card h-100 border p-3">
                        <h6 className="fw-bold">{pkg.packageName}</h6>
                        <h5 className="text-success">₹{pkg.price}</h5>
                        <p className="small text-muted">{pkg.description}</p>
                        
                        {pkg.duration && (
                          <p className="small"><Clock size={14} className="me-1" /> Duration: {pkg.duration}</p>
                        )}
                        
                        <div className="mb-3">
                          {pkg.features?.map((f, k) => (
                            <div key={k} className="small text-secondary mb-1">
                              <CheckCircle size={14} className="text-success me-1" />{f}
                            </div>
                          ))}
                        </div>

                        <button
                          className="btn btn-dark w-100 mt-auto"
                          data-bs-toggle="modal"
                          data-bs-target="#orderModal"
                          onClick={() => setBookingDetails({ ...pkg, eventName: eventGroup.eventName })}
                        >
                          Book Now
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {/* --- ADD THIS MODAL STRUCTURE AT THE BOTTOM --- */}
<div className="modal fade" id="orderModal" tabIndex="-1" aria-hidden="true">
  <div className="modal-dialog modal-dialog-centered">
    <div className="modal-content">
      <div className="modal-header">
        <h5 className="modal-title">Complete Your Booking</h5>
        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" ref={closeBtnRef}></button>
      </div>
      <div className="modal-body">
        {bookingDetails && (
          <div className="mb-3 p-2 bg-light rounded">
            <h6>{bookingDetails.eventName} - <span className="text-primary">{bookingDetails.packageName}</span></h6>
            <p className="mb-0 fw-bold">Price: ₹{bookingDetails.price}</p>
          </div>
        )}

        <form onSubmit={submitHandler}>
          <div className="mb-3">
            <label className="form-label">Full Name</label>
            <input type="text" name="name" className="form-control" required placeholder="John Doe" />
          </div>
          <div className="mb-3">
            <label className="form-label">Phone Number</label>
            <input type="tel" name="phone" className="form-control" required placeholder="1234567890" />
          </div>
          <div className="mb-3">
            <label className="form-label">Message (Optional)</label>
            <textarea name="message" className="form-control" rows="3" placeholder="Any special requirements?"></textarea>
          </div>

          {submitStatus === "error" && (
            <div className="alert alert-danger p-2 small">{serverError}</div>
          )}

          {submitStatus === "success" ? (
            <div className="alert alert-success">Booking Successful! Closing...</div>
          ) : (
            <button 
              type="submit" 
              className="btn btn-dark w-100" 
              disabled={submitStatus === "loading"}
            >
              {submitStatus === "loading" ? "Processing..." : "Confirm Booking"}
            </button>
          )}
        </form>
      </div>
    </div>
  </div>
</div>

          {/* RIGHT SIDEBAR */}
          <div className="col-lg-4">
            <div className="card shadow-sm border-0 sticky-top" style={{ top: '100px' }}>
              <div className="card-body">
                <h5 className="mb-3">Contact Details</h5>
                <p className="mb-2"><Info size={16} className="me-2"/> {selectedVendor.contact?.name}</p>
                <p className="mb-2"><Phone size={16} className="me-2"/> {selectedVendor.contact?.phone}</p>
                <p className="mb-2"><MessageCircle size={16} className="me-2"/> {selectedVendor.contact?.whatsapp}</p>
                <p className="mb-2"><Mail size={16} className="me-2"/> {selectedVendor.contact?.email}</p>
                
                <hr />
                <h6 className="mb-3">Our Locations</h6>
                {selectedVendor.locations?.map((loc, i) => (
                  <div key={i} className="mb-3">
                    <div className="fw-bold"><MapPin size={14} /> {loc.venue}</div>
                    <div className="small text-muted">{loc.address}, {loc.city}</div>
                  </div>
                ))}

                {selectedVendor.locations?.[0]?.city && (
                  <div className="mt-3 rounded overflow-hidden">
                    <iframe
                      width="100%" height="200" style={{ border: 0 }}
                      loading="lazy"
                      src={`https://maps.google.com/maps?q=${selectedVendor.locations[0].city}&output=embed`}
                    ></iframe>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    
  );
};

export default SearchResults;